import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Divider, 
  Grid, 
  IconButton, 
  Typography, 
  useTheme 
} from '@mui/material';
import {
  CardContent, 
  CardListContainer, 
  CarouselContainer, 
  CarouselTrack, 
  CustomCard, 
  ParentContainer, 
} from '../styled';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import { carouselImages } from '../consts';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';

function FeaturedPreview() {
  const theme = useTheme();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const trackRef = useRef(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const cardData = [
    {
      images: [
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/FAW-recipes-pasta-sausage-basil-and-mustard-hero-06-cfd1c0a2989e474ea7e574a38182bbee_ungobd.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/as-bean-bake-kcqv-jumbo_oigghe.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/k_Edit_2024-01-lemon-dill-tortellini-recipe_lemon-dill-tortellini-recipe-5484_nnueeh.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737405200/CloudSous%20Assets/chorizo-mozarella-gnocchi-bake-cropped-9ab73a3_crwdhr.jpg'
      ],
      title: 'Date Night',
    },
    {
      images: [
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/FAW-recipes-pasta-sausage-basil-and-mustard-hero-06-cfd1c0a2989e474ea7e574a38182bbee_ungobd.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/as-bean-bake-kcqv-jumbo_oigghe.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/k_Edit_2024-01-lemon-dill-tortellini-recipe_lemon-dill-tortellini-recipe-5484_nnueeh.jpg',
      ],
      title: 'Meal Prep',
    },
    {
      images: [
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/FAW-recipes-pasta-sausage-basil-and-mustard-hero-06-cfd1c0a2989e474ea7e574a38182bbee_ungobd.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/as-bean-bake-kcqv-jumbo_oigghe.jpg',

      ],
      title: 'Meals for One',
    },
    {
      images: [
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/FAW-recipes-pasta-sausage-basil-and-mustard-hero-06-cfd1c0a2989e474ea7e574a38182bbee_ungobd.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/as-bean-bake-kcqv-jumbo_oigghe.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737765624/k_Edit_2024-01-lemon-dill-tortellini-recipe_lemon-dill-tortellini-recipe-5484_nnueeh.jpg',
        'https://res.cloudinary.com/djrbfvpit/image/upload/v1737405200/CloudSous%20Assets/chorizo-mozarella-gnocchi-bake-cropped-9ab73a3_crwdhr.jpg'
      ],
      title: 'Holiday Dinner',
    }
  ];

  const handleExpandClick = (imageId) => {
    // Navigate to the detailed view page
    navigate(`/detail/${imageId}`);
  };

  const checkScroll = () => {
    if (trackRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const handlePrevClick = () => {
    if (trackRef.current) {
      const scrollAmount = 300; // Adjust this value based on your image width
      trackRef.current.scrollLeft -= scrollAmount;
    }
  };

  const handleNextClick = () => {
    if (trackRef.current) {
      const scrollAmount = 300; // Adjust this value based on your image width
      trackRef.current.scrollLeft += scrollAmount;
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - trackRef.current.offsetLeft);
    setScrollLeft(trackRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - trackRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Adjust scroll speed
    trackRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - trackRef.current.offsetLeft);
    setScrollLeft(trackRef.current.scrollLeft);
  };
  
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - trackRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    trackRef.current.scrollLeft = scrollLeft - walk;
  };

  useEffect(() => {
    const track = trackRef.current;
    if (track) {
      track.addEventListener('scroll', checkScroll);
      // Initial check
      checkScroll();
    }
    return () => {
      if (track) {
        track.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  const QuadImageGrid = ({ images }) => {
    const placeholderColor = theme.palette.secondary.main;
    const imageArray = [...images || [], null, null, null, null].slice(0, 4);
  
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: '2px',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          // borderRadius: '1rem',
          flex: '1',
          padding: '0.5rem',
          maxWidth: '20rem'
        }}
      >
        {imageArray.map((image, index) => (
          <Box
            key={index}
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              backgroundColor: !image ? placeholderColor : 'transparent',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                zIndex: 1,
              },
            }}
          >
            {image ? (
              <img
                src={image}
                alt={`Grid image ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  opacity: 0.7,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative', height: '100%' }}> {/* Wrapper for overlay positioning */}
    {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.palette.secondary.main,
          opacity: 0.9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000, // Ensure it's above everything
          height: '100%'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          Coming Soon
        </Typography>
      </Box>
      <ParentContainer
        sx={{ 
          maxHeight: '80dvh',
          height: '100%',
        }}
      >
        <CarouselContainer>
          {showLeftArrow && (
            <IconButton
              sx={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
              }}
              onClick={handlePrevClick}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <CarouselTrack
            ref={trackRef}
            sx={{
              display: 'flex',
              overflowX: 'hidden',
              scrollBehavior: 'smooth',
              cursor: isDragging ? 'grabbing' : 'grab',
              marginLeft: '1rem'
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
          >
            {carouselImages.concat(carouselImages).map((image, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  height: '100%',
                  minWidth: '300px',
                  marginRight: '1rem',
                  userSelect: 'none',
                }}
              >
                <Box
                  component="img"
                  src={image}
                  sx={{
                    width: '100%',
                    height: '12rem',
                    objectFit: 'cover',
                    borderRadius: '1.5rem'
                  }}
                  draggable={false}
                />
                <Button
                  variant="contained"
                  startIcon={<OpenInFullOutlinedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandClick(index);
                  }}
                  sx={{
                    position: 'absolute',
                    borderRadius: '2rem',
                    bottom: 15,
                    right: 8,
                    backgroundColor: `${theme.palette.primary.main}`,
                    color: 'white',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.5)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    zIndex: 2,
                    textTransform: 'none', // This prevents the text from being all caps
                    padding: '6px 16px', // Adjust padding as needed
                  }}
                >
                  View Recipe
                </Button>
              </Box>
            ))}
          </CarouselTrack>

          {showRightArrow && (
            <IconButton
              sx={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
              }}
              onClick={handleNextClick}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}
        </CarouselContainer>
        <Grid display={'flex'} flexDirection={'column'} sx={{ padding: '1rem'}}>
          <Typography variant='h5' sx={{ marginBottom: '0.5rem' }}>
            Collections
          </Typography>
          <Divider variant='middle' component={'div'} sx={{ width: '100%'}} />
        </Grid>
        <CardListContainer>
          {cardData.map((card, index) => (
            <CustomCard key={index}>
              <QuadImageGrid images={card.images} />
              <CardContent>
                <Typography variant="h5" noWrap>{card.title}</Typography>
              </CardContent>
              <IconButton 
                sx={{ 
                  backgroundColor: `${theme.palette.secondary.main}`,
                  color: `${theme.palette.primary.main}`,
                  right: '1rem', // Distance from right
                }}
              >
                <ArrowForwardIosOutlinedIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </CustomCard>
          ))}
        </CardListContainer>
      </ParentContainer>
    </Box>
  )
}

export default FeaturedPreview;